import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';

import {
  agentDetailsDispositionReportExport,
  agentDetailsDispositionReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallexLoader from '../../../components/CallexLoader';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const AgentDispositionPreview = ({ isOpen, exportDetails, onCloseModal }) => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const isExportDisabled = !checkModulePermission(
    'is_export',
    'Agent Disposition Report',
    userRolePermission
  );

  const handleExportDetails = async () => {
    if (isExportDisabled || isExporting) return;
    setIsExporting(true);
    try {
      const response = await dispatch(agentDetailsDispositionReportExport(exportDetails)).unwrap();
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'disposition-details-report.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log('Error exporting CSV:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const fetchExportDetails = async () => {
    try {
      setLoading(true);
      const response = await dispatch(agentDetailsDispositionReportGet(exportDetails)).unwrap();
      if (response) {
        setReportData(response);
      }
    } catch (error) {
      console.log('Error fetching disposition details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchExportDetails();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={!!isOpen} size="xl" centered backdrop className="report-preview-modal">
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center py-2">
          <span className="fw-bold fs-5">Disposition Details Report</span>
          {reportData?.data?.length > 0 &&
            (isExporting ? (
              <div
                className="me-2 bg-success rounded-2 d-flex justify-content-center align-items-center"
                style={{ width: '30px', height: '30px' }}>
                <Spinner size="sm" color="light" />
              </div>
            ) : (
              <div
                className={`me-2 bg-success rounded-2 d-flex justify-content-center align-items-center ${
                  isExportDisabled ? 'callex-disabled' : ''
                }`}
                style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                onClick={() => handleExportDetails()}>
                <Icon.Download className="text-white" size={18} />
              </div>
            ))}
        </div>
        {loading ? (
          <div
            className="p-0 d-flex justify-content-center align-items-center m-0"
            style={{ height: 'calc(100vh - 215px)' }}>
            <CallexLoader />
          </div>
        ) : reportData?.data?.length > 0 ? (
          <div className="daily-report-table" style={{ height: 'calc(100vh - 215px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val?.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="p-0 d-flex justify-content-center align-items-center no-data m-0"
            style={{ height: 'calc(100vh - 215px)' }}>
            No data available
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Button className="px-5 bg-danger border-danger" onClick={() => onCloseModal()}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AgentDispositionPreview.propTypes = {
  isOpen: PropTypes.any,
  exportDetails: PropTypes.any,
  onCloseModal: PropTypes.func
};

export default AgentDispositionPreview;
